<script setup>
import { ref } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import axios from 'axios'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import ScheduleStats from '@/Components/Teacher/Schedule/ScheduleStats.vue'
import SessionCalendar from '@/Components/Schedule/SessionCalendar.vue'
import CalendarSessionModal from '@/Components/Schedule/CalendarSessionModal.vue'
import CalendarSyncModal from '@/Components/Schedule/CalendarSyncModal.vue'

const props = defineProps({
  stats: Object,
  sessions: Array,
  currentMonth: String,
})

const allSessions = ref([...props.sessions])
const fetchedMonths = ref(new Set())
const isLoadingMonth = ref(false)
const liveStats = ref({ ...props.stats })

// Mark initially loaded months as fetched
function markInitialMonths() {
  props.sessions.forEach(s => {
    if (s.date) {
      fetchedMonths.value.add(s.date.substring(0, 7))
    }
  })
}
markInitialMonths()

const showSessionModal = ref(false)
const selectedSession = ref(null)
const showSyncModal = ref(false)

function handleSessionClick(session) {
  selectedSession.value = session
  showSessionModal.value = true
}

function handleNoteUpdated(updatedSession) {
  const idx = allSessions.value.findIndex(s => s.id === updatedSession.id && s.date === updatedSession.date)
  if (idx >= 0) allSessions.value[idx] = updatedSession
  selectedSession.value = updatedSession
}

async function handleMonthChange({ monthStr }) {
  if (fetchedMonths.value.has(monthStr)) return

  isLoadingMonth.value = true
  try {
    const response = await axios.get(`/teacher/schedules/sessions`, {
      params: { month: monthStr },
    })
    const newSessions = response.data.sessions || []
    // Merge new sessions (avoid duplicates)
    // Real sessions (id > 0): unique by id. Projected (id < 0): unique by series_id+date.
    const sessionKey = (s) => s.id > 0 ? `real:${s.id}` : `proj:${s.series_id}:${s.date}`
    const existingKeys = new Set(allSessions.value.map(sessionKey))
    newSessions.forEach(s => {
      const key = sessionKey(s)
      if (!existingKeys.has(key)) {
        allSessions.value.push(s)
        existingKeys.add(key)
      }
    })
    fetchedMonths.value.add(monthStr)
  } catch (err) {
    console.error('Failed to fetch sessions for month:', monthStr, err)
  } finally {
    isLoadingMonth.value = false
  }
}

function handleStatsChange(stats) {
  liveStats.value = stats
}

function reloadSessions() {
  router.reload({ only: ['sessions', 'stats'] })
}
</script>

<template>
  <Head title="Calendar" />

  <DashboardLayout>
    <div class="space-y-8">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">My Calendar</h1>
        <p class="text-gray-500 text-sm mt-1">View and manage your scheduled sessions</p>
      </div>

      <ScheduleStats :stats="liveStats" />

      <SessionCalendar
        :sessions="allSessions"
        :current-month="currentMonth"
        :show-enrollments="true"
        :is-loading="isLoadingMonth"
        legend-hint="Click on a session for details"
        @session-click="handleSessionClick"
        @month-change="handleMonthChange"
        @stats-change="handleStatsChange"
        @sync-calendar="showSyncModal = true"
      />
    </div>

    <CalendarSessionModal
      :show="showSessionModal"
      :session="selectedSession"
      @close="showSessionModal = false"
      @note-updated="handleNoteUpdated"
      @session-updated="reloadSessions"
      @session-cancelled="reloadSessions"
    />
    <CalendarSyncModal
      :show="showSyncModal"
      @close="showSyncModal = false"
    />
  </DashboardLayout>
</template>
